import { z } from "zod";
import { managerColorSchema } from "./colors";
import { opportunityStatusSchema } from "./opportunities";

export const STAGE_NAME_MAX = 60;

export const stageNameSchema = z
  .string()
  .trim()
  .min(1, "Enter a stage name")
  .max(STAGE_NAME_MAX, `Use at most ${STAGE_NAME_MAX} characters`);

/**
 * Sales stages became configurable in the administration increment.
 *
 * `closedType` is **editable** — the developer's explicit instruction, overriding the
 * safer reading that terminality is system-owned. That makes the guards below load-bearing
 * rather than defensive: opportunity status is *derived* from this mapping, so the mapping
 * has to stay total. At least one active stage of each type must exist at all times, and a
 * stage's type cannot change while it holds opportunities.
 *
 * Several won and several lost stages are allowed — "Closed Lost — No budget" and
 * "Closed Lost — Competitor" are the reason the developer chose an editable type.
 */
export const createStageSchema = z.object({
  name: stageNameSchema,
  /** Defaults to open; a terminal stage can be created directly, or flipped later. */
  closedType: opportunityStatusSchema.default("open"),
});
export type CreateStageInput = z.infer<typeof createStageSchema>;

export const renameStageSchema = z.object({ name: stageNameSchema, version: z.number().int() });
export type RenameStageInput = z.infer<typeof renameStageSchema>;

export const setStageTypeSchema = z.object({
  closedType: opportunityStatusSchema,
  version: z.number().int(),
});
export type SetStageTypeInput = z.infer<typeof setStageTypeSchema>;

/**
 * A stage's colour, from the one curated palette (`MANAGER_COLORS`).
 *
 * One palette rather than two: these keys are already contrast-verified against both themes,
 * and a stage sharing a hue with a manager is not a collision — they never share a column, and
 * the chip carries the stage's name as its text. `null` clears it, and an uncoloured stage
 * falls back to the tone its `closedType` implies.
 */
export const setStageColorSchema = z.object({
  color: managerColorSchema.nullable(),
  version: z.number().int(),
});
export type SetStageColorInput = z.infer<typeof setStageColorSchema>;

export const setStageActiveSchema = z.object({ active: z.boolean(), version: z.number().int() });
export type SetStageActiveInput = z.infer<typeof setStageActiveSchema>;

/**
 * Reorder is one transactional write carrying the **whole** ordered list.
 *
 * Not a per-stage `displayOrder` PATCH: the order is unique among active stages, so moving
 * one row past another through two independent writes trips the constraint halfway. One
 * gesture, one transaction, one version check per row.
 */
export const reorderStagesSchema = z.object({
  stages: z
    .array(z.object({ id: z.string().uuid(), version: z.number().int() }))
    .min(1, "Send the full ordered list"),
});
export type ReorderStagesInput = z.infer<typeof reorderStagesSchema>;

/**
 * A stage as an administrator sees it — the read the board uses, plus what deciding
 * whether an operation is even allowed requires.
 */
export const adminStageSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  displayOrder: z.number().int(),
  closedType: opportunityStatusSchema,
  /** Null until assigned; the chip then falls back to the tone `closedType` implies. */
  color: managerColorSchema.nullable(),
  active: z.boolean(),
  version: z.number().int(),
  /** Non-deleted opportunities in this stage — what blocks a type change or deactivation. */
  opportunityCount: z.number().int(),
  /**
   * Derived server-side, so the interface can explain a refusal *before* it happens rather
   * than offering a control that will be rejected. The API enforces the same rules
   * regardless of what was rendered.
   */
  canChangeType: z.boolean(),
  canDeactivate: z.boolean(),
});
export type AdminStage = z.infer<typeof adminStageSchema>;

// ---------------------------------------------------------------------------
// Configuration settings
// ---------------------------------------------------------------------------

export const settingSchema = z.object({
  key: z.string(),
  value: z.string(),
  version: z.number().int(),
  modifiedAt: z.string(),
  modifiedByName: z.string().nullable(),
});
export type Setting = z.infer<typeof settingSchema>;

export const updateSettingSchema = z.object({ value: z.string(), version: z.number().int() });
export type UpdateSettingInput = z.infer<typeof updateSettingSchema>;
